import { useQuery } from '@tanstack/react-query';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import type { PaginationVariables } from '~settings/api/types/paginationVariables';
import type { UserTypeMap } from '~settings/constants/UserTypeMap';

import { RoleQueryKeys } from '../config/queryKeys';
import { BFFClient } from '../lib/BFFClient';
import {
  type UserRoleListResponse,
  UserRoleListResponseSchema,
} from '../lib/schemas';
import type { PresetUseQueryOptions } from '../lib/types';

export interface UserRolesQueryVariables extends PaginationVariables {
  targetUserType?: keyof typeof UserTypeMap;
}

const EXTERNAL_USER_ROLES = {
  total: 3,
  page: 1,
  totalPages: 1,
  data: [
    { id: 'labeladmin', name: 'LABEL_ADMIN' },
    { id: 'videocoordinator', name: 'VIDEO_COORDINATOR' },
    { id: 'releasecoordinator', name: 'RELEASE_COORDINATOR' },
  ],
};

const INTERNAL_USER_ROLES = {
  total: 6,
  page: 1,
  totalPages: 1,
  data: [
    { id: 'opsadmin', name: 'OPS_ADMIN' },
    { id: 'techadmin', name: 'TECH_ADMIN' },
    { id: 'releasemanager', name: 'RELEASE_MANAGER' },
    { id: 'releasecoordinator', name: 'RELEASE_COORDINATOR' },
    { id: 'videomanager', name: 'VIDEO_MANAGER' },
    { id: 'supportadmin', name: 'SUPPORT_ADMIN' },
  ],
};

const OLD_USER_ROLES = {
  total: 8,
  page: 1,
  totalPages: 1,
  data: [
    ...EXTERNAL_USER_ROLES.data,
    ...INTERNAL_USER_ROLES.data.filter(({ id }) => id !== 'releasecoordinator'),
  ],
};

export function useUserRoles<TData extends UserRoleListResponse>(
  variables: UserRolesQueryVariables,
  options?: PresetUseQueryOptions<UserRoleListResponse, TData>,
) {
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  const { data, ...query } = useQuery({
    queryKey: RoleQueryKeys.roles({ skip: 0, take: 100, ...variables }),
    queryFn: async () => {
      if (!isInternalUserUpdatesEnabled) {
        let roles = OLD_USER_ROLES as TData;

        if (variables.targetUserType === 'EXTERNAL') {
          roles = EXTERNAL_USER_ROLES as TData;
        }

        if (variables.targetUserType === 'INTERNAL') {
          roles = INTERNAL_USER_ROLES as TData;
        }

        return new Promise<TData>((resolve) => {
          setTimeout(
            () => resolve(UserRoleListResponseSchema.parse(roles) as TData),
            100,
          );
        });
      }

      const responseData = await BFFClient.get('roles', {
        searchParams: { ...variables },
      }).json();

      return UserRoleListResponseSchema.parse(responseData);
    },
    ...options,
  });

  return { userRoles: data, ...query };
}
